import React, { useState } from 'react'
import { Link, usePage } from '@inertiajs/react'

export default function Header() {
  const { auth, url } = usePage().props
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : (url || '/')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState(null)

  const signedIn = auth?.signed_in
  const user = auth?.user
  const isAdmin = user?.permission === 'admin'

  const isActive = (href) => {
    if (href === '/') return currentPath === '/'
    return currentPath.startsWith(href)
  }

  // Navigation items configuration
  const mainNav = [
    {
      label: 'sobre',
      href: '/sobre',
      dropdown: [
        { label: 'pesquisa', href: '/sobre' },
        { label: 'equipe', href: '/sobre#equipe' },
        { label: 'parceria', href: '/sobre#parceria' },
      ]
    },
    { label: 'mapa', href: signedIn ? '/mapa' : '/users/sign_up' },
    { label: 'ranking', href: signedIn ? '/ranking' : '/users/sign_up' },
    ...(signedIn ? [{ label: 'polos', href: '/polos' }] : []),
    {
      label: 'publicações',
      href: '/publicacoes',
      dropdown: [
        { label: 'acadêmicas', href: '/publicacoes' },
        { label: 'imprensa', href: '/imprensa' },
      ]
    },
    ...(signedIn ? [{ label: 'metodologia', href: '/metodologia' }] : []),
  ]

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200">
      <div className="h-16 px-6 flex items-center justify-between max-w-7xl mx-auto">
        {/* Logo */}
        <Link href="/" className="flex-shrink-0">
          <img
            src="/images/IMAGEM 00 - LOGO LARANJA.png"
            alt="Cidade e Varejo"
            className="h-7 w-auto"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-1">
          {mainNav.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => item.dropdown && setActiveDropdown(item.label)}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <Link
                href={item.href}
                className={`
                  relative px-3 py-2 text-sm transition-colors
                  ${isActive(item.href)
                    ? 'text-gray-900 font-medium'
                    : 'text-gray-600 hover:text-gray-900'
                  }
                `}
              >
                {item.label}
                {isActive(item.href) && (
                  <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-brand-dark rounded-full" />
                )}
              </Link>

              {/* Dropdown */}
              {item.dropdown && activeDropdown === item.label && (
                <div className="absolute top-full left-0 pt-1">
                  <div className="bg-white border border-gray-200 rounded-md shadow-sm py-1 min-w-[140px]">
                    {item.dropdown.map((subItem) => (
                      <Link
                        key={subItem.href}
                        href={subItem.href}
                        className="block px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                      >
                        {subItem.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Right side - Auth */}
        <div className="hidden lg:flex items-center gap-4">
          {!signedIn ? (
            <>
              <Link
                href="/users/sign_in"
                className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                entrar
              </Link>
              <Link
                href="/users/sign_up"
                className="text-sm px-4 py-2 bg-brand-dark text-white rounded hover:bg-brand-dark/90 transition-colors"
              >
                registrar-se
              </Link>
            </>
          ) : (
            <div className="flex items-center gap-3">
              {isAdmin && (
                <Link
                  href="/admin"
                  className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
                >
                  admin
                </Link>
              )}
              <Link
                href={`/users/${user?.id}`}
                className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                {user?.nome}
              </Link>
              <Link
                href="/users/sign_out"
                method="delete"
                as="button"
                className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
              >
                sair
              </Link>
            </div>
          )}
        </div>

        {/* Mobile menu button */}
        <button
          type="button"
          className="lg:hidden p-2 -mr-2 text-gray-600 hover:text-gray-900 transition-colors"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {mobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100">
          <nav className="px-6 py-4 space-y-1">
            {mainNav.map((item) => (
              <div key={item.label}>
                <Link
                  href={item.href}
                  className={`
                    block py-2 text-sm transition-colors
                    ${isActive(item.href)
                      ? 'text-gray-900 font-medium'
                      : 'text-gray-600'
                    }
                  `}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
                {item.dropdown && (
                  <div className="pl-4 space-y-1">
                    {item.dropdown.map((subItem) => (
                      <Link
                        key={subItem.href}
                        href={subItem.href}
                        className="block py-1.5 text-sm text-gray-500 hover:text-gray-700"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {subItem.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Mobile auth section */}
            <div className="pt-4 mt-4 border-t border-gray-100">
              {!signedIn ? (
                <div className="space-y-2">
                  <Link
                    href="/users/sign_in"
                    className="block py-2 text-sm text-gray-600"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    entrar
                  </Link>
                  <Link
                    href="/users/sign_up"
                    className="block py-2 text-sm text-gray-900 font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    registrar-se
                  </Link>
                </div>
              ) : (
                <div className="space-y-2">
                  <Link
                    href={`/users/${user?.id}`}
                    className="block py-2 text-sm text-gray-600"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {user?.nome}
                  </Link>
                  {isAdmin && (
                    <Link
                      href="/admin"
                      className="block py-2 text-sm text-gray-500"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      admin
                    </Link>
                  )}
                  <Link
                    href="/users/sign_out"
                    method="delete"
                    as="button"
                    className="block py-2 text-sm text-gray-500"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    sair
                  </Link>
                </div>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  )
}
